/**
 * Blog Pagination Component
 * Handles pagination for blog articles with arrows navigation
 * Copyright (c) 2025 Delanoe Pirard / Aedelon. Apache 2.0 License.
 */

import { utils } from '../core/utils.js';

export class BlogPagination {
  constructor() {
    this.blogGrid = document.querySelector('.blog-grid');
    this.blogCards = document.querySelectorAll('.blog-card');

    // Pagination settings
    this.currentPage = 1;
    this.articlesPerPage = this.getArticlesPerPage();
    this.paginationContainer = null;

    this.init();
  }

  init() {
    if (!this.blogGrid || this.blogCards.length === 0) return;

    // Only setup pagination if more than threshold
    if (this.blogCards.length > this.getMaxVisibleArticles()) {
      this.createPaginationContainer();
      this.setupResponsivePagination();
      this.updatePagination();
      this.showCurrentPage();
    }
  }

  getArticlesPerPage() {
    const width = window.innerWidth;
    if (width <= 576) return 1;   // Mobile: 1 per page
    if (width <= 992) return 2;   // Tablet: 2 per page
    return 4;                      // Desktop: 4 per page
  }

  getMaxVisibleArticles() {
    // Threshold to show pagination (more than one page)
    return this.getArticlesPerPage();
  }

  createPaginationContainer() {
    // Create arrows (inside grid for proper centering)
    const prevArrow = document.createElement('button');
    prevArrow.className = 'pagination-arrow blog-pagination-prev';
    prevArrow.setAttribute('aria-label', 'Previous articles');
    prevArrow.disabled = true;
    prevArrow.innerHTML = '<i class="fas fa-chevron-left"></i>';

    const nextArrow = document.createElement('button');
    nextArrow.className = 'pagination-arrow blog-pagination-next';
    nextArrow.setAttribute('aria-label', 'Next articles');
    nextArrow.innerHTML = '<i class="fas fa-chevron-right"></i>';

    // Insert arrows inside the grid
    this.blogGrid.appendChild(prevArrow);
    this.blogGrid.appendChild(nextArrow);

    // Create pagination info (after grid)
    const paginationInfoHTML = `
      <div class="blog-pagination">
        <div class="pagination-info">
          <span class="current-page">1</span>
          <span class="separator">/</span>
          <span class="total-pages">1</span>
        </div>
      </div>
    `;
    this.blogGrid.insertAdjacentHTML('afterend', paginationInfoHTML);
    this.paginationContainer = document.querySelector('.blog-pagination');

    // Bind events
    this.bindPaginationEvents();
  }

  bindPaginationEvents() {
    const prevBtn = this.blogGrid.querySelector('.blog-pagination-prev');
    const nextBtn = this.blogGrid.querySelector('.blog-pagination-next');

    prevBtn?.addEventListener('click', () => this.goToPage(this.currentPage - 1));
    nextBtn?.addEventListener('click', () => this.goToPage(this.currentPage + 1));

    // Swipe gestures for mobile
    this.setupSwipeGestures();
  }

  setupSwipeGestures() {
    let touchStartX = 0;
    let touchEndX = 0;

    this.blogGrid.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });

    this.blogGrid.addEventListener('touchend', (e) => {
      touchEndX = e.changedTouches[0].screenX;
      this.handleSwipe(touchStartX, touchEndX);
    }, { passive: true });
  }

  handleSwipe(startX, endX) {
    const swipeThreshold = 50;
    const diff = startX - endX;

    if (Math.abs(diff) > swipeThreshold) {
      if (diff > 0) {
        this.goToPage(this.currentPage + 1); // Swipe left - next
      } else {
        this.goToPage(this.currentPage - 1); // Swipe right - prev
      }
    }
  }

  updatePagination() {
    const totalPages = Math.ceil(this.blogCards.length / this.articlesPerPage);

    // Update pagination info
    const currentPageSpan = this.paginationContainer?.querySelector('.current-page');
    const totalPagesSpan = this.paginationContainer?.querySelector('.total-pages');

    if (currentPageSpan) currentPageSpan.textContent = this.currentPage;
    if (totalPagesSpan) totalPagesSpan.textContent = totalPages;

    // Update button states
    const prevBtn = this.blogGrid.querySelector('.blog-pagination-prev');
    const nextBtn = this.blogGrid.querySelector('.blog-pagination-next');

    if (prevBtn) {
      prevBtn.disabled = this.currentPage === 1;
      prevBtn.style.display = totalPages <= 1 ? 'none' : 'flex';
    }
    if (nextBtn) {
      nextBtn.disabled = this.currentPage >= totalPages;
      nextBtn.style.display = totalPages <= 1 ? 'none' : 'flex';
    }

    // Hide pagination info if only one page
    if (this.paginationContainer) {
      this.paginationContainer.style.display = totalPages <= 1 ? 'none' : 'flex';
    }
  }

  showCurrentPage() {
    const startIndex = (this.currentPage - 1) * this.articlesPerPage;
    const endIndex = startIndex + this.articlesPerPage;

    this.blogCards.forEach((card, index) => {
      const shouldShow = index >= startIndex && index < endIndex;
      card.style.display = shouldShow ? 'flex' : 'none';
    });
  }

  goToPage(page) {
    const totalPages = Math.ceil(this.blogCards.length / this.articlesPerPage);

    if (page < 1 || page > totalPages) return;

    this.currentPage = page;
    this.updatePagination();
    this.showCurrentPage();
  }

  setupResponsivePagination() {
    const handleResize = utils.debounce(() => {
      const newArticlesPerPage = this.getArticlesPerPage();

      if (newArticlesPerPage !== this.articlesPerPage) {
        this.articlesPerPage = newArticlesPerPage;
        this.currentPage = 1;
        this.updatePagination();
        this.showCurrentPage();
      }
    }, 250);

    window.addEventListener('resize', handleResize);
  }
}
